import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Plus, Search, Edit, Trash2, Eye, X, Mail, MessageSquare, Bell, Star, FileCheck, Copy, Code, Globe } from 'lucide-react';

interface Template {
  id: number;
  name: string;
  type: string;
  subject: string;
  body: string;
  variables?: string;
  language: string;
  isActive: boolean;
  isDefault: boolean;
  category: string;
  branchId?: number;
  branchName?: string;
  createdAt: string;
}

const T = {
  title: "القوالب",
  subtitle: "إدارة قوالب الرسائل والإشعارات",
  createNew: "إنشاء قالب جديد",
  totalTemplates: "إجمالي القوالب",
  activeTemplates: "القوالب النشطة",
  defaultTemplates: "القوالب الافتراضية",
  categories: "الفئات",
  search: "بحث باسم القالب...",
  loading: "جاري التحميل...",
  noTemplates: "لا توجد قوالب",
  thName: "اسم القالب",
  thType: "النوع",
  thSubject: "الموضوع",
  thCategory: "الفئة",
  thStatus: "الحالة",
  thActions: "الإجراءات",
  view: "عرض",
  edit: "تعديل",
  delete: "حذف",
  setDefault: "تعيين افتراضي",
  preview: "معاينة",
  editTemplate: "تعديل القالب",
  createTemplate: "إنشاء قالب جديد",
  viewTemplate: "تفاصيل القالب",
  lblName: "اسم القالب",
  lblType: "نوع القالب",
  lblSubject: "الموضوع",
  lblBody: "محتوى القالب",
  lblVariables: "المتغيرات",
  lblLanguage: "اللغة",
  lblCategory: "الفئة",
  cancel: "إلغاء",
  update: "تحديث",
  create: "إنشاء",
  close: "إغلاق",
  confirmDelete: "هل أنت متأكد من حذف هذا القالب؟",
  updated: "تم تحديث القالب بنجاح",
  created: "تم إنشاء القالب بنجاح",
  deleted: "تم حذف القالب بنجاح",
  setDefaultSuccess: "تم تعيين القالب كافتراضي بنجاح",
  error: "حدث خطأ",
  active: "نشط",
  inactive: "غير نشط",
  default: "افتراضي",
  typeEmail: "بريد إلكتروني",
  typeSms: "رسالة نصية",
  typeNotification: "إشعار",
  typeInvoice: "فاتورة",
  typeReport: "تقرير",
  allTypes: "جميع الأنواع"
};

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [form, setForm] = useState({
    name: '', type: 'Email', subject: '', body: '',
    variables: '', language: 'ar', category: 'System',
    isActive: true, isDefault: false
  });

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/templates?pageSize=100');
      if (res.data?.success) {
        const data = res.data.data;
        setTemplates(Array.isArray(data) ? data : (data.items || []));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setForm({ name: '', type: 'Email', subject: '', body: '', variables: '', language: 'ar', category: 'System', isActive: true, isDefault: false });
    setShowModal(true);
  };

  const openEdit = (t: Template) => {
    setEditingTemplate(t);
    setForm({
      name: t.name || '',
      type: t.type || 'Email',
      subject: t.subject || '',
      body: t.body || '',
      variables: t.variables || '',
      language: t.language || 'ar',
      category: t.category || 'System',
      isActive: t.isActive,
      isDefault: t.isDefault
    });
    setShowModal(true);
  };

  const openView = (t: Template) => { setSelectedTemplate(t); setShowViewModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        type: form.type,
        subject: form.subject,
        body: form.body,
        variables: form.variables,
        language: form.language,
        category: form.category,
        isActive: form.isActive,
        isDefault: form.isDefault
      };
      if (editingTemplate) {
        await api.put('/api/templates/' + editingTemplate.id, payload);
        alert(T.updated);
      } else {
        await api.post('/api/templates', payload);
        alert(T.created);
      }
      setShowModal(false);
      loadTemplates();
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      await api.delete('/api/templates/' + id);
      loadTemplates();
      alert(T.deleted);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await api.put('/api/templates/' + id + '/set-default');
      loadTemplates();
      alert(T.setDefaultSuccess);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const types = Array.from(new Set(templates.map(t => t.type).filter(Boolean)));
  const filtered = templates.filter(t => {
    const matchSearch = (t.name || '').toLowerCase().includes(search.toLowerCase()) ||
                       (t.subject || '').toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'all' || t.type === typeFilter;
    return matchSearch && matchType;
  });

  const activeTemplates = templates.filter(t => t.isActive).length;
  const defaultTemplates = templates.filter(t => t.isDefault).length;
  const categories = Array.from(new Set(templates.map(t => t.category).filter(Boolean)));
  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 transition';

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Email': return Mail;
      case 'SMS': return MessageSquare;
      case 'Notification': return Bell;
      case 'Invoice': return FileCheck;
      case 'Report': return FileText;
      default: return FileText;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'Email': return T.typeEmail;
      case 'SMS': return T.typeSms;
      case 'Notification': return T.typeNotification;
      case 'Invoice': return T.typeInvoice;
      case 'Report': return T.typeReport;
      default: return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Email': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'SMS': return 'bg-green-50 text-green-700 border-green-200';
      case 'Notification': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Invoice': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'Report': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>{T.createNew}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center"><FileText className="w-5 h-5 text-teal-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalTemplates}</p>
          <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><FileCheck className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">{T.activeTemplates}</p>
          <p className="text-2xl font-bold text-green-700">{activeTemplates}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center"><Star className="w-5 h-5 text-yellow-600" /></div></div>
          <p className="text-sm text-gray-500">{T.defaultTemplates}</p>
          <p className="text-2xl font-bold text-yellow-700">{defaultTemplates}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><Globe className="w-5 h-5 text-purple-600" /></div></div>
          <p className="text-sm text-gray-500">{T.categories}</p>
          <p className="text-2xl font-bold text-purple-700">{categories.length}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder={T.search} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 transition" />
            </div>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500">
              <option value="all">{T.allTypes}</option>
              {types.map(type => <option key={type} value={type}>{getTypeLabel(type)}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div><p className="text-gray-500 mt-4">{T.loading}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{T.noTemplates}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thName}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thType}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thSubject}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thCategory}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thStatus}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((template) => {
                    const TypeIcon = getTypeIcon(template.type);
                    return (
                      <tr key={template.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white"><TypeIcon className="w-5 h-5" /></div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-gray-900">{template.name}</p>
                                {template.isDefault && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1"><Star className="w-3 h-3" />{T.default}</span>}
                              </div>
                              <p className="text-xs text-gray-500">{template.language === 'ar' ? 'عربي' : template.language === 'en' ? 'English' : template.language}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-5"><span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getTypeColor(template.type)}`}><TypeIcon className="w-3 h-3" />{getTypeLabel(template.type)}</span></td>
                        <td className="py-4 px-5"><span className="text-gray-700 text-sm truncate max-w-xs block">{template.subject}</span></td>
                        <td className="py-4 px-5"><span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">{template.category || '-'}</span></td>
                        <td className="py-4 px-5">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${template.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {template.isActive ? T.active : T.inactive}
                          </span>
                        </td>
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openView(template)} className="p-2 hover:bg-teal-50 rounded-lg text-gray-500 hover:text-teal-600 transition" title={T.view}><Eye className="w-4 h-4" /></button>
                            <button onClick={() => openEdit(template)} className="p-2 hover:bg-teal-50 rounded-lg text-gray-500 hover:text-teal-600 transition" title={T.edit}><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handleSetDefault(template.id)} className="p-2 hover:bg-yellow-50 rounded-lg text-gray-500 hover:text-yellow-600 transition" title={T.setDefault}><Star className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(template.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title={T.delete}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingTemplate ? T.editTemplate : T.createTemplate}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{T.lblName} *</Label><Input value={form.name} onChange={(e: any) => setForm({...form, name: e.target.value})} required className={inputClass} /></div>
                <div><Label>{T.lblType} *</Label><select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className={inputClass}><option value="Email">{T.typeEmail}</option><option value="SMS">{T.typeSms}</option><option value="Notification">{T.typeNotification}</option><option value="Invoice">{T.typeInvoice}</option><option value="Report">{T.typeReport}</option></select></div>
                <div className="md:col-span-2"><Label>{T.lblSubject} *</Label><Input value={form.subject} onChange={(e: any) => setForm({...form, subject: e.target.value})} required className={inputClass} /></div>
                <div className="md:col-span-2"><Label>{T.lblBody} *</Label><textarea value={form.body} onChange={(e: any) => setForm({...form, body: e.target.value})} required className={inputClass + ' font-mono text-sm'} rows={8} placeholder="..." /></div>
                <div className="md:col-span-2"><Label>{T.lblVariables}</Label><Input value={form.variables} onChange={(e: any) => setForm({...form, variables: e.target.value})} className={inputClass} placeholder="customerName,orderNumber,trackingNumber" /></div>
                <div><Label>{T.lblLanguage}</Label><select value={form.language} onChange={(e) => setForm({...form, language: e.target.value})} className={inputClass}><option value="ar">عربي</option><option value="en">English</option></select></div>
                <div><Label>{T.lblCategory}</Label><Input value={form.category} onChange={(e: any) => setForm({...form, category: e.target.value})} className={inputClass} /></div>
                <div className="flex items-center gap-2"><input type="checkbox" id="tplActive" checked={form.isActive} onChange={(e) => setForm({...form, isActive: e.target.checked})} className="w-4 h-4" /><label htmlFor="tplActive" className="text-sm font-medium">{T.active}</label></div>
                <div className="flex items-center gap-2"><input type="checkbox" id="tplDefault" checked={form.isDefault} onChange={(e) => setForm({...form, isDefault: e.target.checked})} className="w-4 h-4" /><label htmlFor="tplDefault" className="text-sm font-medium">{T.default}</label></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">{T.cancel}</Button>
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">{editingTemplate ? T.update : T.create}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}

      {showViewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{T.viewTemplate}</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 pb-4 border-b mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white">{(() => { const Icon = getTypeIcon(selectedTemplate.type); return <Icon className="w-8 h-8" />; })()}</div>
              <div><h3 className="text-xl font-bold">{selectedTemplate.name}</h3><div className="flex items-center gap-2 mt-1"><span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getTypeColor(selectedTemplate.type)}`}>{getTypeLabel(selectedTemplate.type)}</span>{selectedTemplate.isDefault && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1"><Star className="w-3 h-3" />{T.default}</span>}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">{T.lblSubject}</p><p className="font-semibold">{selectedTemplate.subject}</p></div>
              <div><p className="text-sm text-gray-500">{T.lblCategory}</p><p className="font-semibold">{selectedTemplate.category || '-'}</p></div>
              <div><p className="text-sm text-gray-500">{T.lblLanguage}</p><p className="font-semibold">{selectedTemplate.language === 'ar' ? 'عربي' : selectedTemplate.language === 'en' ? 'English' : selectedTemplate.language}</p></div>
              <div><p className="text-sm text-gray-500">{T.thStatus}</p><span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedTemplate.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{selectedTemplate.isActive ? T.active : T.inactive}</span></div>
              {selectedTemplate.variables && <div className="col-span-2"><p className="text-sm text-gray-500">{T.lblVariables}</p><div className="flex flex-wrap gap-2 mt-1">{selectedTemplate.variables.split(',').map((v, i) => <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-mono">{v.trim()}</span>)}</div></div>}
              <div className="col-span-2"><p className="text-sm text-gray-500 mb-2">{T.lblBody}</p><div className="bg-gray-50 p-4 rounded-xl border border-gray-200"><pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">{selectedTemplate.body}</pre></div></div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => { setShowViewModal(false); openEdit(selectedTemplate); }} className="flex-1 bg-teal-600 hover:bg-teal-700"><Edit className="w-4 h-4 ml-2" />{T.edit}</Button>
              <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">{T.close}</Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
